// packages block
import { Box, Button, Pagination, TextField, Typography } from '@mui/material'
import { ChangeEvent, FC, KeyboardEvent, useEffect, useState } from 'react'
// others block
import { PaginationWithJumpProps } from '../../interfaceTypes'
import { customPaginationStyles } from '../../theme/styleConstants'

// Theme's MuiButton padding overrides size="small", so input and button share an explicit height.
const JUMP_CONTROL_HEIGHT = { small: 28, medium: 32 }

/**
 * MUI Pagination plus a "Go to page" input. The jump goes through the same
 * `onChange` as the page buttons, so callers keep a single navigation path.
 */
export const PaginationWithJump: FC<PaginationWithJumpProps> = ({ count, page, onChange, disabled, size = 'medium', sx }) => {
  const [jumpValue, setJumpValue] = useState('')
  const [error, setError] = useState('')

  // A filter can shrink `count` under a typed value; drop the stale input and error.
  useEffect(() => {
    setJumpValue('')
    setError('')
  }, [count])

  const handleJumpChange = (event: ChangeEvent<HTMLInputElement>) => {
    setJumpValue(event.target.value.replace(/\D/g, ''))
    setError('')
  }

  const handleJump = () => {
    if (!jumpValue) return

    const target = Number(jumpValue)
    if (target < 1 || target > count) {
      setError(`Enter 1–${count}`)
      return
    }

    setJumpValue('')
    if (target !== page) onChange(target)
  }

  const handleJumpKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    // Not a <form>: listings can sit inside a parent form, and Enter must not submit it.
    event.preventDefault()
    handleJump()
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="wrap"
      gap={2}
      pt={3}
      pb={3}
      px={2}
      sx={[customPaginationStyles, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Pagination
        color="primary"
        disabled={disabled}
        count={count}
        page={page}
        shape="rounded"
        size={size}
        onChange={(_, newPage) => onChange(newPage)}
      />

      {count > 1 && (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            Go to page
          </Typography>

          <Box position="relative">
            <TextField
              size="small"
              value={jumpValue}
              onChange={handleJumpChange}
              onKeyDown={handleJumpKeyDown}
              disabled={disabled}
              error={!!error}
              placeholder={`1–${count}`}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Go to page', sx: { py: 0, fontSize: '14px' } }}
              InputProps={{ sx: { height: JUMP_CONTROL_HEIGHT[size] } }}
              sx={{ width: 84 }}
            />
            {error && (
              <Typography variant="caption" color="error" whiteSpace="nowrap" position="absolute" top="100%" left={0}>
                {error}
              </Typography>
            )}
          </Box>

          <Button
            size="small"
            variant="outlined"
            disabled={disabled || !jumpValue}
            onClick={handleJump}
            sx={{ height: JUMP_CONTROL_HEIGHT[size], minWidth: 0, py: 0 }}
          >
            Go
          </Button>
        </Box>
      )}
    </Box>
  )
}
